import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useNoindexNofollow } from '../hooks/useNoindex';


const API = '/api/account';

/* ---------- Статусы заказов ---------- */
const ORDER_STATUS_LABELS = {
    CREATED: 'Создан',
    PENDING_PAYMENT: 'Ожидает оплаты',
    PAID: 'Оплачен',
    SHIPPED: 'Отправлен',
    DELIVERED: 'Доставлен',
    RETURN_REQUESTED: 'Возврат запрошен',
    RETURNED: 'Возврат',
    REFUNDED: 'Деньги возвращены',
    CANCELLED: 'Отменён',
};

const ORDER_STATUS_STYLES = {
    CREATED: 'bg-gray-100 text-gray-700',
    PENDING_PAYMENT: 'bg-amber-100 text-amber-800',
    PAID: 'bg-[#6B7A6A] text-white',
    SHIPPED: 'bg-blue-100 text-blue-800',
    DELIVERED: 'bg-emerald-100 text-emerald-800',
    RETURN_REQUESTED: 'bg-orange-100 text-orange-800',
    RETURNED: 'bg-purple-100 text-purple-800',
    REFUNDED: 'bg-purple-100 text-purple-700',
    CANCELLED: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }) {
    const cls = ORDER_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';
    const label = ORDER_STATUS_LABELS[status] || status || '—';
    return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
            {label}
        </span>
    );
}

/* ---------- Карточка заказа в списке ---------- */
function OrderCard({ order, onOpen }) {
    const date = order.created_at
        ? new Date(order.created_at).toLocaleString('ru-RU', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
        : '—';

    return (
        <button
            onClick={() => onOpen(order)}
            className="w-full text-left bg-white rounded-2xl border border-[#E8E3DD] p-5 hover:border-[#6B7A6A] hover:shadow-md transition"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <p className="text-xs text-gray-500 mb-1">Заказ</p>
                    <p className="font-mono text-sm font-medium text-[#1C1917] truncate">
                        {order.order_id}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{date}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <StatusBadge status={order.status} />
                    <p className="text-lg font-semibold text-[#1C1917] whitespace-nowrap">
                        {(order.amount / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                    </p>
                </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0ECE8] text-xs text-gray-500">
                <span>
                    {order.items_count} {order.items_count === 1 ? 'товар' : order.items_count < 5 ? 'товара' : 'товаров'}
                </span>
                <span className="text-[#6B7A6A] font-medium">Открыть →</span>
            </div>
        </button>
    );
}

/* ---------- Модалка с деталями заказа ---------- */
function OrderDetailModal({ orderId, onClose, onRepeat, onCancel, busy }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let stop = false;
        setLoading(true);
        fetch(`${API}/orders/${orderId}`, { credentials: 'include' })
            .then((r) => {
                if (!r.ok) throw new Error(`Ошибка ${r.status}`);
                return r.json();
            })
            .then((d) => { if (!stop) setData(d); })
            .catch((e) => { if (!stop) setErr(e.message || 'Не удалось загрузить заказ'); })
            .finally(() => { if (!stop) setLoading(false); });
        return () => { stop = true; };
    }, [orderId]);

    const canCancel = data && (data.status === 'CREATED' || data.status === 'PENDING_PAYMENT');

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full sm:rounded-3xl rounded-t-3xl shadow-2xl max-w-2xl max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-[#E8E3DD] px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-xs text-gray-500">Заказ</p>
                        <p className="font-mono text-sm font-medium">{orderId}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
                </div>

                <div className="p-6 space-y-5">
                    {loading && <p className="text-gray-500 text-sm">Загрузка…</p>}
                    {err && <p className="text-red-500 text-sm">{err}</p>}

                    {data && (
                        <>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <StatusBadge status={data.status} />
                                <p className="text-xl font-semibold">
                                    {(data.amount / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                </p>
                            </div>

                            {data.created_at && (
                                <p className="text-xs text-gray-500">
                                    Оформлен: {new Date(data.created_at).toLocaleString('ru-RU')}
                                </p>
                            )}

                            <div>
                                <p className="text-sm font-semibold mb-2">Состав заказа</p>
                                <div className="space-y-2">
                                    {data.items.map((it, i) => (
                                        <div key={i} className="flex items-start justify-between gap-3 text-sm py-2 border-b border-[#F0ECE8] last:border-0">
                                            <div className="min-w-0 flex-1">
                                                {it.product_id !== 'delivery' ? (
                                                    <Link
                                                        to={`/product/${it.product_id}`}
                                                        onClick={onClose}
                                                        className="hover:text-[#6B7A6A] hover:underline"
                                                    >
                                                        {it.name}
                                                    </Link>
                                                ) : (
                                                    <span>{it.name}</span>
                                                )}
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {(it.price / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽ × {it.quantity}
                                                </p>
                                            </div>
                                            <span className="font-medium whitespace-nowrap">
                                                {(it.sum / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {data.delivery_address && (
                                <div>
                                    <p className="text-sm font-semibold mb-1">Доставка</p>
                                    <p className="text-sm text-gray-700">{data.delivery_address}</p>
                                </div>
                            )}

                            {data.tracking_number && (
                                <div className="bg-[#F5F0EB] rounded-xl p-4">
                                    <p className="text-sm font-semibold mb-1">
                                        📦 Трек-номер {data.delivery_service ? `(${data.delivery_service})` : ''}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <code className="text-sm font-mono text-[#1C1917] break-all">
                                            {data.tracking_number}
                                        </code>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard?.writeText(data.tracking_number);
                                                toast.success('Трек скопирован');
                                            }}
                                            className="shrink-0 text-xs text-[#6B7A6A] underline hover:text-[#1C1917]"
                                        >
                                            копировать
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Отслеживайте доставку в приложении {data.delivery_service || 'службы доставки'}.
                                    </p>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-[#E8E3DD]">
                                <button
                                    onClick={() => onRepeat(orderId)}
                                    disabled={busy}
                                    className="btn-primary flex-1 py-3 rounded-lg disabled:opacity-50"
                                >
                                    {busy ? 'Обработка…' : '🔁 Повторить заказ'}
                                </button>
                                {canCancel && (
                                    <button
                                        onClick={() => onCancel(orderId)}
                                        disabled={busy}
                                        className="px-5 py-3 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition text-sm disabled:opacity-50"
                                    >
                                        Отменить заказ
                                    </button>
                                )}
                            </div>

                            <p className="text-xs text-gray-400 text-center">
                                Вопросы по заказу? Напишите нам на{' '}
                                <a href="mailto:sale@puresynergy.ru" className="underline">sale@puresynergy.ru</a>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- Секция «Согласия и данные» (152-ФЗ) ---------- */
function ConsentsSection({ userEmail }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deletionRequested, setDeletionRequested] = useState(false);

    useEffect(() => {
        let stop = false;
        fetch('/api/account/profile', { credentials: 'include' })
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (!stop && d) setProfile(d); })
            .catch(() => { })
            .finally(() => { if (!stop) setLoading(false); });
        return () => { stop = true; };
    }, []);

    const toggleMarketing = async () => {
        if (!profile || saving) return;
        setSaving(true);
        try {
            const r = await fetch('/api/account/consents', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ marketing_consent: !profile.marketing_consent }),
            });
            if (r.ok) {
                const d = await r.json();
                setProfile((p) => ({ ...p, marketing_consent: d.marketing_consent }));
                toast.success(d.marketing_consent ? 'Вы подписаны на рассылку' : 'Подписка отключена');
            } else {
                toast.error('Не удалось сохранить');
            }
        } catch (_) {
            toast.error('Ошибка сети');
        } finally {
            setSaving(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const r = await fetch('/api/account/export', { credentials: 'include' });
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `puresynergy-data-${userEmail}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Файл скачивается');
        } catch (_) {
            toast.error('Не удалось скачать данные');
        } finally {
            setExporting(false);
        }
    };

    const handleRequestDeletion = async () => {
        if (!window.confirm(
            'Запросить удаление персональных данных? Запрос обрабатывается в течение 30 дней. ' +
            'Заказы будут обезличены, но сохранятся 5 лет по требованию налогового учёта.'
        )) return;
        try {
            const r = await fetch('/api/account/request-deletion', {
                method: 'POST',
                credentials: 'include',
            });
            if (r.ok) {
                setDeletionRequested(true);
                toast.success('Запрос отправлен');
            } else {
                toast.error('Не удалось отправить запрос');
            }
        } catch (_) {
            toast.error('Ошибка сети');
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-6 mb-6 text-sm text-gray-500">
                Загрузка согласий…
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-[#E8E3DD] p-6 mb-8">
            <h2 className="serif text-xl text-[#1C1917] mb-4">Согласия и данные</h2>

            {/* Маркетинговое согласие */}
            <label className="flex items-start gap-3 cursor-pointer mb-4">
                <input
                    type="checkbox"
                    checked={!!profile?.marketing_consent}
                    onChange={toggleMarketing}
                    disabled={saving}
                    className="mt-0.5"
                />
                <span className="text-sm text-gray-700">
                    Хочу получать письма об акциях и новинках.
                    <span className="block text-xs text-gray-500 mt-0.5">
                        Отписаться можно в любой момент — эта галочка или ссылка в письме.
                    </span>
                </span>
            </label>

            {/* Экспорт данных */}
            <div className="border-t border-[#F0ECE8] pt-4 mt-4">
                <p className="text-sm font-medium mb-2">Мои данные</p>
                <p className="text-xs text-gray-500 mb-3">
                    Согласно 152-ФЗ вы можете получить копию всех данных, которые мы о вас храним.
                    В файл попадёт профиль, история заказов и журнал входов.
                </p>
                <button
                    onClick={handleExport}
                    disabled={exporting}
                    className="btn-outline text-sm py-2 px-4 disabled:opacity-50"
                >
                    {exporting ? 'Готовлю файл…' : '📥 Скачать мои данные'}
                </button>
            </div>

            {/* Удаление */}
            <div className="border-t border-[#F0ECE8] pt-4 mt-4">
                <p className="text-sm font-medium mb-2">Удаление данных</p>
                {deletionRequested ? (
                    <p className="text-xs text-[#6B7A6A]">
                        ✅ Запрос отправлен. Мы свяжемся с вами в течение 30 дней.
                    </p>
                ) : (
                    <>
                        <p className="text-xs text-gray-500 mb-3">
                            Хотите, чтобы мы удалили ваши персональные данные?
                            Заказы будут обезличены, но сохранятся 5 лет по требованию налогового учёта.
                        </p>
                        <button
                            onClick={handleRequestDeletion}
                            className="text-sm text-red-600 underline hover:text-red-700"
                        >
                            Запросить удаление данных
                        </button>
                    </>
                )}
            </div>

            {/* Информация об операторе */}
            <div className="border-t border-[#F0ECE8] pt-4 mt-4 text-xs text-gray-500">
                <p>
                    Оператор персональных данных:{' '}
                    <strong className="text-gray-700">
                        ИП Кодацкая Е.А., ОГРНИП 326508100445985, ИНН 501216827208
                    </strong>
                </p>
                <p className="mt-1">
                    По вопросам обработки ПДн: <a href="mailto:sale@puresynergy.ru" className="underline">sale@puresynergy.ru</a>
                    {' · '}
                    <a href="/privacy" className="underline">Политика обработки ПДн</a>
                </p>
            </div>
        </div>
    );
}

/* ---------- Секция «Бонусы» ---------- */
function BonusSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        let stop = false;
        fetch('/api/account/bonus', { credentials: 'include' })
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (!stop && d) setData(d); })
            .catch(() => { })
            .finally(() => { if (!stop) setLoading(false); });
        return () => { stop = true; };
    }, []);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-6 mb-6 text-sm text-gray-500">
                Загрузка бонусов…
            </div>
        );
    }
    if (!data) return null;

    const TYPE_LABELS = {
        earn: { label: 'Начислено', color: 'text-emerald-600', sign: '+' },
        spend: { label: 'Списано', color: 'text-red-500', sign: '' },
        reversal: { label: 'Аннулировано (отмена)', color: 'text-red-500', sign: '' },
        expire: { label: 'Сгорело', color: 'text-gray-500', sign: '' },
        manual_credit: { label: 'Начислено администратором', color: 'text-emerald-600', sign: '+' },
        manual_debit: { label: 'Списано администратором', color: 'text-red-500', sign: '' },
        refund: { label: 'Возврат', color: 'text-emerald-600', sign: '+' },
    };

    const history = data.history || [];
    const shown = showAll ? history : history.slice(0, 5);

    return (
        <div className="bg-white rounded-2xl border border-[#E8E3DD] p-6 mb-8">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                    <h2 className="serif text-xl text-[#1C1917] mb-1">Бонусы</h2>
                    <p className="text-xs text-gray-500">
                        {data.program.earn_percent}% от каждой покупки · 1 балл = 1 ₽ · сгорают через {data.program.ttl_days / 30} мес.
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-3xl font-bold text-[#6B7A6A]">💎 {data.balance}</p>
                    <p className="text-xs text-gray-500">доступно к списанию</p>
                </div>
            </div>

            {history.length > 0 ? (
                <>
                    <div className="border-t border-[#F0ECE8] pt-3 space-y-1">
                        {shown.map((t) => {
                            const meta = TYPE_LABELS[t.type] || { label: t.type, color: 'text-gray-600', sign: '' };
                            const amt = t.amount;
                            const isPositive = amt > 0;
                            return (
                                <div key={t.id} className="flex items-start justify-between gap-3 py-1.5 text-sm">
                                    <div className="min-w-0">
                                        <p className="truncate">{meta.label}</p>
                                        {t.reason && (
                                            <p className="text-xs text-gray-400 truncate">{t.reason}</p>
                                        )}
                                        <p className="text-[10px] text-gray-400">
                                            {t.created_at && new Date(t.created_at).toLocaleString('ru-RU')}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`font-medium ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {isPositive ? '+' : ''}{amt} ₽
                                        </p>
                                        <p className="text-[10px] text-gray-400">баланс: {t.balance_after}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {history.length > 5 && (
                        <button
                            onClick={() => setShowAll((v) => !v)}
                            className="mt-2 text-xs text-[#6B7A6A] underline hover:text-[#1C1917]"
                        >
                            {showAll ? 'Свернуть' : `Показать все (${history.length})`}
                        </button>
                    )}
                </>
            ) : (
                <p className="text-sm text-gray-500 border-t border-[#F0ECE8] pt-3">
                    У вас пока нет операций с бонусами. Совершите первую покупку — начислим {data.program.earn_percent}% от её суммы.
                </p>
            )}
        </div>
    );
}

/* ---------- Секция «Мои адреса» ---------- */
function AddressesSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);   // null | id
    const [form, setForm] = useState({ label: '', address: '', network: 'wb', is_default: false });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch('/api/account/addresses', { credentials: 'include' });
            if (r.ok) {
                const d = await r.json();
                setItems(d.addresses || []);
            }
        } catch (_) { }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openNew = () => {
        setEditing(null);
        setForm({ label: '', address: '', network: 'wb', is_default: items.length === 0 });
        setShowForm(true);
    };
    const openEdit = (a) => {
        setEditing(a.id);
        setForm({
            label: a.label || '',
            address: a.address || '',
            network: a.network || 'wb',
            is_default: !!a.is_default,
        });
        setShowForm(true);
    };

    const save = async (e) => {
        e.preventDefault();
        if (form.address.trim().length < 5) {
            toast.error('Укажите корректный адрес');
            return;
        }
        setSaving(true);
        try {
            const url = editing
                ? `/api/account/addresses/${editing}`
                : '/api/account/addresses';
            const method = editing ? 'PATCH' : 'POST';
            const r = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: form.label.trim(),
                    address: form.address.trim(),
                    network: form.network,
                    is_default: form.is_default,
                }),
            });
            if (!r.ok) throw new Error('Не удалось сохранить');
            toast.success(editing ? 'Адрес обновлён' : 'Адрес добавлен');
            setShowForm(false);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.message || 'Ошибка');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Удалить адрес «${a.label || a.address}»?`)) return;
        try {
            const r = await fetch(`/api/account/addresses/${a.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) throw new Error();
            toast.success('Адрес удалён');
            load();
        } catch (_) {
            toast.error('Не удалось удалить');
        }
    };

    const makeDefault = async (a) => {
        if (a.is_default) return;
        try {
            const r = await fetch(`/api/account/addresses/${a.id}/default`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!r.ok) throw new Error();
            load();
        } catch (_) {
            toast.error('Не удалось');
        }
    };

    const inputCls = 'w-full p-2 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A]';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div className="bg-white rounded-2xl border border-[#E8E3DD] p-6 mb-8">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <h2 className="serif text-xl text-[#1C1917]">Мои адреса</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Сохраните 2–3 адреса — при оформлении заказа они подставятся автоматически.
                    </p>
                </div>
                <button
                    onClick={openNew}
                    className="text-sm font-medium text-[#6B7A6A] border border-[#6B7A6A] rounded-full px-4 py-2 hover:bg-[#6B7A6A] hover:text-white transition"
                >
                    + Добавить адрес
                </button>
            </div>

            {loading && <p className="text-sm text-gray-500">Загрузка…</p>}

            {!loading && items.length === 0 && !showForm && (
                <p className="text-sm text-gray-500">
                    Пока нет сохранённых адресов. Добавьте первый — пригодится при следующем заказе.
                </p>
            )}

            <div className="space-y-2">
                {items.map((a) => (
                    <div
                        key={a.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition ${a.is_default ? 'border-[#6B7A6A] bg-[#FBF9F6]' : 'border-[#E8E3DD] bg-white'}`}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-sm font-medium text-[#1C1917]">
                                    {a.label || 'Адрес'}
                                </span>
                                {a.network && (
                                    <span className="text-[10px] uppercase tracking-widest text-[#6B7A6A] bg-[#F5F0EB] px-1.5 py-0.5 rounded">
                                        {a.network}
                                    </span>
                                )}
                                {a.is_default && (
                                    <span className="text-[10px] font-semibold text-white bg-[#6B7A6A] px-1.5 py-0.5 rounded">
                                        основной
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-600 break-words">{a.address}</p>
                        </div>
                        <div className="shrink-0 flex flex-col gap-1 text-xs text-right">
                            {!a.is_default && (
                                <button onClick={() => makeDefault(a)} className="text-[#6B7A6A] underline hover:text-[#1C1917]">
                                    сделать основным
                                </button>
                            )}
                            <button onClick={() => openEdit(a)} className="text-gray-500 underline hover:text-[#1C1917]">
                                изменить
                            </button>
                            <button onClick={() => remove(a)} className="text-red-500 underline hover:text-red-600">
                                удалить
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showForm && (
                <form onSubmit={save} className="mt-4 border-t border-[#F0ECE8] pt-4 space-y-3">
                    <div>
                        <label className={labelCls}>Название (необязательно)</label>
                        <input
                            value={form.label}
                            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                            placeholder="Дом, Работа, ПВЗ у метро"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Служба доставки</label>
                        <select
                            value={form.network}
                            onChange={(e) => setForm((f) => ({ ...f, network: e.target.value }))}
                            className={inputCls}
                        >
                            <option value="wb">Wildberries</option>
                            <option value="ozon">Ozon</option>
                            <option value="">Другое</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Адрес *</label>
                        <input
                            value={form.address}
                            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                            placeholder="Город, улица, дом"
                            className={inputCls}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.is_default}
                            onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                        />
                        Сделать основным адресом
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
                            {saving ? 'Сохранение…' : (editing ? 'Обновить' : 'Добавить')}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setShowForm(false); setEditing(null); }}
                            className="btn-outline text-sm py-2 px-4"
                        >
                            Отмена
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

/* ---------- Главная страница ЛК ---------- */
export default function AccountPage({ addToCart, openCart, products }) {
    useNoindexNofollow();
    const { user, loading: authLoading, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [openOrderId, setOpenOrderId] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const r = await fetch(`${API}/orders`, { credentials: 'include' });
            if (!r.ok) throw new Error(`Ошибка ${r.status}`);
            const data = await r.json();
            setOrders(data.orders || []);
        } catch (e) {
            toast.error('Не удалось загрузить заказы');
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) loadOrders();
    }, [isAuthenticated, loadOrders]);

    const handleCancel = useCallback(async (orderId) => {
        if (!window.confirm('Отменить заказ? Это действие нельзя отменить.')) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/orders/${orderId}/cancel`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Не удалось отменить');
            }
            toast.success('Заказ отменён');
            setOpenOrderId(null);
            loadOrders();
        } catch (e) {
            toast.error(e.message || 'Ошибка отмены');
        } finally {
            setBusy(false);
        }
    }, [loadOrders]);

    const handleRepeat = useCallback(async (orderId) => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/orders/${orderId}/repeat`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!r.ok) throw new Error('Не удалось получить состав заказа');
            const data = await r.json();

            if (!data.items?.length) {
                toast.error('Товары из этого заказа больше не продаются');
                return;
            }

            // Ищем товары в каталоге и добавляем в корзину
            const byId = new Map((products || []).map((p) => [String(p.id), p]));
            let added = 0;
            let missing = 0;
            for (const { id, qty } of data.items) {
                const product = byId.get(String(id));
                if (!product) { missing++; continue; }
                for (let i = 0; i < qty; i++) {
                    addToCart(product);
                    added++;
                }
            }

            if (added > 0) {
                toast.success(`Добавлено ${added} ${added === 1 ? 'товар' : added < 5 ? 'товара' : 'товаров'} в корзину`);
                setOpenOrderId(null);
                setTimeout(() => openCart?.(), 300);
            } else {
                toast.error('Не удалось добавить товары');
            }
            if (missing > 0 && added > 0) {
                toast(`⚠️ ${missing} ${missing === 1 ? 'товар' : 'товаров'} больше не в каталоге`, { icon: '⚠️' });
            }
        } catch (e) {
            toast.error(e.message || 'Ошибка');
        } finally {
            setBusy(false);
        }
    }, [products, addToCart, openCart]);

    if (authLoading) {
        return <div className="max-w-3xl mx-auto py-20 text-center text-gray-500">Загрузка…</div>;
    }

    if (!isAuthenticated) {
        return (
            <div className="max-w-md mx-auto py-20 text-center">
                <p className="text-gray-600 mb-4">Вы не авторизованы</p>
                <Link to="/account/login" state={{ from: '/account' }} className="btn-primary inline-block">
                    Войти
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-10 px-4">
            <h1 className="serif text-3xl text-[#1C1917] mb-6">Личный кабинет</h1>

            {/* Карточка пользователя */}
            <div className="bg-white rounded-2xl border border-[#E8E3DD] p-5 mb-8 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs text-gray-500 mb-1">Вы вошли как</p>
                    <p className="font-medium text-[#1C1917]">{user.email}</p>
                </div>
                <Link
                    to="/account/wishlist"
                    className="text-sm font-medium text-[#6B7A6A] border border-[#6B7A6A] rounded-full px-4 py-2 hover:bg-[#6B7A6A] hover:text-white transition"
                >
                    ❤️ Избранное
                </Link>
            </div>

            {/* Мои заказы */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="serif text-2xl text-[#1C1917]">Мои заказы</h2>
                <button
                    onClick={loadOrders}
                    disabled={ordersLoading}
                    className="text-xs text-gray-500 hover:text-[#1C1917] underline disabled:opacity-50"
                >
                    {ordersLoading ? 'Загрузка…' : 'Обновить'}
                </button>
            </div>

            {ordersLoading && !orders.length && (
                <div className="bg-white rounded-2xl border border-[#E8E3DD] p-8 text-center text-sm text-gray-500">
                    Загрузка…
                </div>
            )}

            {!ordersLoading && orders.length === 0 && (
                <div className="bg-[#F5F0EB] rounded-2xl p-8 text-center">
                    <p className="text-3xl mb-3">🛍️</p>
                    <p className="text-gray-700 mb-4">У вас пока нет заказов</p>
                    <Link to="/" className="btn-primary inline-block">Перейти в каталог</Link>
                </div>
            )}

            {orders.length > 0 && (
                <div className="space-y-3">
                    {orders.map((o) => (
                        <OrderCard key={o.order_id} order={o} onOpen={(x) => setOpenOrderId(x.order_id)} />
                    ))}
                </div>
            )}

            {/* Бонусы */}
            <BonusSection />

            {/* Мои адреса */}
            <AddressesSection />

            {/* Согласия и данные */}
            <ConsentsSection userEmail={user.email} />

            {/* Выход */}
            <div className="mt-10 text-center">
                <button
                    onClick={async () => { await logout(); navigate('/'); }}
                    className="text-sm text-gray-500 underline hover:text-[#1C1917]"
                >
                    Выйти
                </button>
            </div>

            {/* Модалка заказа */}
            {openOrderId && (
                <OrderDetailModal
                    orderId={openOrderId}
                    onClose={() => setOpenOrderId(null)}
                    onRepeat={handleRepeat}
                    onCancel={handleCancel}
                    busy={busy}
                />
            )}
        </div>
    );
}